/**
 * QuizListItem Component
 * Modern card-row hybrid layout for quiz items
 */

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiFileText, FiUsers, FiCalendar, FiClock, FiEdit3, FiEye, FiTrash2 } from 'react-icons/fi';
import OverflowMenu from '../common/OverflowMenu';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';

const QuizCard = styled(motion.div)`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: 1.25rem 1.5rem;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  &:hover {
    border-color: var(--primary-color);
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
`;

const QuizContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const QuizTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const QuizDescription = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  
  p {
    margin: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
    -webkit-line-clamp: 1;
  }
`;

const QuizStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const StatChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--pill-radius);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
  white-space: nowrap;
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: var(--text-muted);
  }
`;

const QuizMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  min-width: 140px;
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: auto;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: var(--text-muted);
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius);
  background: var(--primary-color);
  color: var(--text-inverse);
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
  }
`;

function QuizListItem({ 
  quiz, 
  onAssign, 
  onEdit, 
  onPreview, 
  onDelete,
  isAssigned = false,
  index = 0
}) {
  

  const formatDateTime = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const menuItems = [
    {
      id: 'edit',
      label: 'Edit',
      icon: <FiEdit3 />,
      onClick: onEdit
    },
    {
      id: 'preview',
      label: 'Preview',
      icon: <FiEye />,
      onClick: onPreview
    },
    {
      id: 'divider',
      divider: true
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <FiTrash2 />,
      onClick: onDelete,
      danger: true
    }
  ];

  const description = quiz.description 
    ? sanitizeHtmlForViewer(quiz.description).replace(/<[^>]*>/g, '').trim()
    : 'No description provided';

  return (
    <QuizCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <QuizContent>
        <QuizTitle>{quiz.title}</QuizTitle>
        
        {description && (
          <QuizDescription dangerouslySetInnerHTML={{ 
            __html: sanitizeHtmlForViewer(description.substring(0, 150) + (description.length > 150 ? '...' : ''))
          }} />
        )}
        
        <QuizStats>
          <StatChip>
            <FiFileText />
            {quiz.question_count || 0} questions
          </StatChip>
          {quiz.assignment_count > 0 && (
            <StatChip>
              <FiUsers />
              {quiz.assignment_count} assignments
            </StatChip>
          )}
        </QuizStats>
      </QuizContent>
      
      <QuizMeta>
        <MetaItem>
          <FiCalendar />
          Created {formatDateTime(quiz.created_at)}
        </MetaItem>
        {quiz.updated_at && (
          <MetaItem>
            <FiClock />
            Updated {formatDateTime(quiz.updated_at)}
          </MetaItem>
        )}
      </QuizMeta>
      
      <ActionsContainer>
        <PrimaryButton
          onClick={onAssign}
          aria-label={isAssigned ? 'View quiz' : 'Assign quiz'}
        >
          {isAssigned ? 'View' : 'Assign'}
        </PrimaryButton>
        <OverflowMenu 
          items={menuItems}
          ariaLabel={`More options for ${quiz.title}`}
        />
      </ActionsContainer>
    </QuizCard>
  );
}

export default QuizListItem;
